import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listLaborRates, upsertLaborRate, deleteLaborRate } from "@/lib/labor.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/labor/rates")({ component: RatesPage });

const COMPONENTS = ["labor_pick", "labor_pack", "labor_receive", "packaging", "freight", "storage_per_day"] as const;

function RatesPage() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listLaborRates);
  const upsertFn = useServerFn(upsertLaborRate);
  const delFn = useServerFn(deleteLaborRate);

  const { data: rates } = useQuery({
    queryKey: ["labor-rates", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const [component, setComponent] = useState<typeof COMPONENTS[number]>("labor_pick");
  const [rate, setRate] = useState(2500); // ¢/hr ($25)
  const [effective, setEffective] = useState(() => new Date().toISOString().slice(0, 10));

  const add = useMutation({
    mutationFn: () => upsertFn({ data: { tenantId: tenantId!, component, rateCentsPerHour: rate, effectiveFrom: effective } }),
    onSuccess: () => { toast.success("Rate added"); qc.invalidateQueries({ queryKey: ["labor-rates"] }); },
    onError: (e: Error) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id: string) => delFn({ data: { tenantId: tenantId!, id } }),
    onSuccess: () => { toast.success("Removed"); qc.invalidateQueries({ queryKey: ["labor-rates"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <>
      <PageHeader title="Labor Cost Rates" description="Hourly fully-loaded rates per activity, used for cost-to-serve and incentives." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Card>
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Component</Label>
              <Select value={component} onValueChange={(v) => setComponent(v as typeof COMPONENTS[number])}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COMPONENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Rate (¢/hour)</Label>
              <Input type="number" min={0} value={rate} onChange={(e) => setRate(Math.max(0, Number(e.target.value) || 0))} />
              <div className="text-[11px] text-muted-foreground">= ${(rate / 100).toFixed(2)}/hr</div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Effective from</Label>
              <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
            </div>
            <Button onClick={() => add.mutate()} disabled={add.isPending || !tenantId}>Add rate</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Component</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead className="text-right">Rate / hour</TableHead>
                  <TableHead>Effective from</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rates ?? []).length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No rates yet.</TableCell></TableRow>}
                {(rates ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="text-sm">{r.component}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.facility_id ? r.facility_id.slice(0, 8) : "All"}</TableCell>
                    <TableCell className="text-right tabular-nums">${(r.rate_cents_per_hour / 100).toFixed(2)}</TableCell>
                    <TableCell className="text-sm">{r.effective_from}</TableCell>
                    <TableCell>
                      <Button size="icon" variant="ghost" onClick={() => del.mutate(r.id)} disabled={del.isPending}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}